import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Camera, Sparkles, ArrowRight, RefreshCw } from "lucide-react";

const PARCHMENT = "https://static.prod-images.emergentagent.com/jobs/39141d99-ef4f-4686-a207-2702e1fdeb56/images/71961a88075b238f2c3ac01d6af1c6efc499931b1c198a38074b5530f81c88c1.png";

const MOOD_LABEL = {
  happy: "Joyful",
  calm: "At peace",
  sad: "Tender",
  angry: "Stirred",
  neutral: "Quiet",
  no_face: "No face detected",
};

const MOOD_COLOR = {
  happy: "#3F6B3A",
  calm: "#3F6B3A",
  sad: "#5C6B5F",
  angry: "#C05B3B",
  neutral: "#5C6B5F",
  no_face: "#8A948C",
};

export default function Reflect() {
  const { refresh } = useAuth();
  const [avatars, setAvatars] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stream, setStream] = useState(null);
  const [streamError, setStreamError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Load avatars
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/avatars");
        setAvatars(data);
        if (data.length) setSelected(data[0]);
      } catch (e) { setError(formatApiError(e)); }
    })();
  }, []);

  // Start camera when avatar selected and no result yet
  const startCamera = async () => {
    setStreamError("");
    setResult(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: false,
      });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play();
      }
    } catch (e) {
      setStreamError(
        e?.message?.includes("Permission")
          ? "Please allow camera access in your browser to continue."
          : `Could not access camera: ${e?.message || e}`
      );
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
  };

  useEffect(() => () => stopCamera(), []); // cleanup on unmount

  const captureAndSend = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    setBusy(true);
    setError("");
    try {
      const v = videoRef.current;
      const c = canvasRef.current;
      const w = v.videoWidth || 640;
      const h = v.videoHeight || 480;
      c.width = w;
      c.height = h;
      const ctx = c.getContext("2d");
      ctx.drawImage(v, 0, 0, w, h);
      const dataUrl = c.toDataURL("image/jpeg", 0.85);
      const b64 = dataUrl.split(",")[1];

      const { data } = await api.post("/reflect", {
        image_b64: b64,
        avatar_id: selected?.id,
      });
      setResult(data);
      stopCamera();
      // refresh user state so navbar JSK badge updates immediately
      refresh && refresh();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setResult(null); setError(""); };

  const avatarUrl = (a) => (a?.url ? `${BACKEND_URL}${a.url}` : "");

  return (
    <div data-testid="reflect-page" className="max-w-6xl mx-auto px-6 lg:px-12 py-14">
      <div className="animate-fade-up">
        <span className="tag inline-flex items-center gap-2">
          <Sparkles size={14} /> Reflect with Krishna
        </span>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-[#2D3B31] mt-5 leading-tight">
          A glance, a verse, a blessing.
        </h1>
        <p className="mt-5 text-[#5C6B5F] leading-relaxed max-w-2xl">
          Choose a form of Krishna to sit with, share a brief still from your camera,
          and receive a verse chosen for the mood you carry today. One reflection per day.
        </p>
      </div>

      {/* Step 1: avatar select */}
      <section className="mt-12">
        <div className="text-xs uppercase tracking-widest text-[#D96C4A] mb-4">
          Step 1 — Choose a presence
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="avatar-grid">
          {avatars.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelected(a)}
              className={`organic-card p-3 text-left transition-all ${
                selected?.id === a.id ? "ring-2 ring-[#D96C4A] -translate-y-1" : ""
              }`}
              data-testid={`avatar-${a.id}`}
            >
              <div className="aspect-square rounded-xl overflow-hidden bg-[#F3EFE9]">
                <img src={avatarUrl(a)} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="mt-3">
                <div className="font-serif-display text-base text-[#2D3B31] leading-tight">{a.name}</div>
                <div className="text-[10px] tracking-widest uppercase text-[#8A948C] mt-1">{a.subtitle}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Step 2: camera */}
      <section className="mt-14">
        <div className="text-xs uppercase tracking-widest text-[#D96C4A] mb-4">
          Step 2 — A still moment
        </div>

        {!result && (
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="organic-card p-6">
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[#2D3B31] flex items-center justify-center">
                {stream ? (
                  <video ref={videoRef} className="w-full h-full object-cover" playsInline muted data-testid="reflect-video" />
                ) : (
                  <div className="text-center text-[#FAF8F5] p-6">
                    <Camera size={42} className="mx-auto opacity-70" />
                    <p className="mt-3 text-sm opacity-80">Camera is off</p>
                  </div>
                )}
              </div>
              <canvas ref={canvasRef} className="hidden" />
              <div className="mt-5 flex flex-wrap gap-3">
                {!stream ? (
                  <button onClick={startCamera} className="btn-accent inline-flex items-center gap-2" data-testid="reflect-start-camera">
                    <Camera size={16} /> Turn on camera
                  </button>
                ) : (
                  <>
                    <button onClick={captureAndSend} disabled={busy || !selected} className="btn-accent inline-flex items-center gap-2" data-testid="reflect-capture">
                      <Sparkles size={16} />
                      {busy ? "Reading the moment…" : "Capture & reflect"}
                    </button>
                    <button onClick={stopCamera} className="btn-outline" data-testid="reflect-stop-camera">
                      Cancel
                    </button>
                  </>
                )}
              </div>
              {streamError && <p className="mt-4 text-sm text-[#C05B3B]" data-testid="reflect-stream-error">{streamError}</p>}
              {error && <p className="mt-4 text-sm text-[#C05B3B]" data-testid="reflect-error">{error}</p>}
              <p className="mt-5 text-xs text-[#8A948C] leading-relaxed">
                Your photo is analyzed by Claude vision and discarded — only the resulting mood and matched
                verse are saved (so you can see your past reflections). One reflection counts toward JSK
                blessings per day.
              </p>
            </div>
            <div className="organic-card p-3 hidden md:block">
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[#F3EFE9]">
                {selected ? (
                  <img src={avatarUrl(selected)} alt={selected.name} className="w-full h-full object-cover" data-testid="reflect-selected-avatar" />
                ) : null}
              </div>
              {selected && (
                <div className="p-4">
                  <div className="font-serif-display text-xl text-[#2D3B31]">{selected.name}</div>
                  <div className="text-xs uppercase tracking-widest text-[#8A948C] mt-1">{selected.subtitle}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="animate-fade-up" data-testid="reflect-result">
            <div className="grid md:grid-cols-12 gap-6">
              <div className="md:col-span-5 organic-card p-3">
                <div className="aspect-square rounded-xl overflow-hidden">
                  {selected && <img src={avatarUrl(selected)} alt={selected.name} className="w-full h-full object-cover" />}
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-widest text-[#8A948C]">In the company of</div>
                  <div className="font-serif-display text-2xl text-[#2D3B31] mt-1">{selected?.name}</div>
                </div>
              </div>
              <div className="md:col-span-7">
                <div
                  className="relative rounded-[2rem] overflow-hidden border p-8 md:p-10"
                  style={{ borderColor: "#EAE5D9", backgroundImage: `url(${PARCHMENT})`, backgroundSize: "cover" }}
                >
                  <div className="absolute inset-0 bg-white/85" />
                  <div className="relative">
                    <div className="text-xs uppercase tracking-widest" style={{ color: MOOD_COLOR[result.mood] || "#5C6B5F" }} data-testid="reflect-mood">
                      Today's mood: {MOOD_LABEL[result.mood] || result.mood} · {result.confidence} confidence
                    </div>
                    {result.delta_applied !== 0 && (
                      <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F2D8CF] text-[#C05B3B] text-xs font-medium" data-testid="reflect-points">
                        <Sparkles size={12} />
                        {result.delta_applied > 0 ? `+${result.delta_applied}` : result.delta_applied} JSK Blessing Points
                      </div>
                    )}
                    {result.already_today && (
                      <div className="mt-2 text-xs text-[#8A948C]" data-testid="reflect-already-today">
                        You've already reflected today — points unchanged.
                      </div>
                    )}

                    {result.verse && (
                      <div className="mt-7 border-l-4 pl-5 py-2" style={{ borderColor: "#D96C4A" }}>
                        <div className="text-xs tracking-widest uppercase text-[#D96C4A]">Gītā {result.verse.reference}</div>
                        {result.verse.sanskrit && (
                          <p className="shloka mt-2 text-xl text-[#2D3B31]">{result.verse.sanskrit}</p>
                        )}
                        <p className="mt-2 text-base italic text-[#2D3B31] font-serif-display">
                          “{result.verse.translation}”
                        </p>
                      </div>
                    )}

                    {result.blessing && (
                      <div className="mt-8 text-base leading-relaxed text-[#2D3B31] whitespace-pre-wrap" data-testid="reflect-blessing">
                        {result.blessing}
                      </div>
                    )}

                    <div className="mt-10 flex gap-3 flex-wrap">
                      <button onClick={reset} className="btn-outline inline-flex items-center gap-2" data-testid="reflect-again">
                        <RefreshCw size={14} /> Reflect again
                      </button>
                      <Link to="/chapters" className="btn-accent inline-flex items-center gap-2">
                        Read the chapter <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
